import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  MenuItem,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import { frFR } from '@mui/x-data-grid/locales';
import { DeleteOutline, GetApp, Sync } from '@mui/icons-material';
import { useLocation, useNavigate } from 'react-router-dom';
import PartnerMainContainer from '../../../components/PartnerMainContainer';
import PartnerService from '../../../services/PartnerService';
import Message from '../../tools/Message';
import TitlePartner from '../component/Title';
import SynchronizeDialog from '../Import/SynchronizeDialog';
import {
  partnerDataGridSx,
  partnerFilterButtonSx,
  partnerFilterFieldSx,
  partnerTableColors,
} from '../Parterns/partnerListTheme';
import databaseStyles from './database.styles';

const isSyncedPeriod = (value) => value === true || value === 1 || value === '1';
const formatCount = (value) => Number(value || 0).toLocaleString('fr-FR');
const readText = (value, fallback = 'Non renseigné') => String(value || '').trim() || fallback;
const isReadyPeriod = (period) => (
  period.import_status === 'READY_FOR_SYNC'
  && (period.is_sync === 0 || period.is_sync === '0')
);
const STATUS_LABELS = {
  ALL: 'Tous les statuts',
  READY_FOR_SYNC: 'Prête à synchroniser',
  SYNCED: 'Synchronisée',
  UPLOADED: 'Fichier reçu',
  ANALYZING: 'Analyse en cours',
  FAILED: 'Analyse échouée',
  ACTION_REQUIRED: 'Correction requise',
  SYNCING: 'Synchronisation en cours',
};
const getStatusFromSearch = (search) => {
  const status = new URLSearchParams(search).get('status') || 'READY_FOR_SYNC';
  return Object.prototype.hasOwnProperty.call(STATUS_LABELS, status) ? status : 'READY_FOR_SYNC';
};

const formatDate = (value) => {
  if (!value) return 'Non renseignée';
  const date = new Date(value);
  return Number.isNaN(date.getTime())
    ? 'Non renseignée'
    : date.toLocaleDateString('fr-FR', { day: '2-digit', month: 'short', year: 'numeric' });
};

const DatabaseView = () => {
  const classes = databaseStyles();
  const location = useLocation();
  const navigate = useNavigate();
  const hasLoadedPeriods = useRef(false);
  const handledNavigationKey = useRef(null);
  const [periods, setPeriods] = useState([]);
  const [statusFilter, setStatusFilter] = useState(() => getStatusFromSearch(location.search));
  const [selectedIds, setSelectedIds] = useState([]);
  const [fileFilter, setFileFilter] = useState('');
  const [periodFilter, setPeriodFilter] = useState('');
  const [importerFilter, setImporterFilter] = useState('');
  const [paginationModel, setPaginationModel] = useState({ page: 0, pageSize: 10 });
  const [isLoading, setIsLoading] = useState(true);
  const [syncIds, setSyncIds] = useState([]);
  const [periodToRemove, setPeriodToRemove] = useState(null);
  const [removingPeriodId, setRemovingPeriodId] = useState(null);
  const [message, setMessage] = useState({ open: false, color: 'green', messageInfo: '' });

  const loadPeriods = useCallback(async ({ showLoader = true } = {}) => {
    if (showLoader) setIsLoading(true);
    try {
      const result = await PartnerService.getPeriods();
      const nextPeriods = result?.status && Array.isArray(result.data) ? result.data : [];
      setPeriods(nextPeriods);
      const readyIds = nextPeriods.filter(isReadyPeriod).map((period) => Number(period.part_period_id));
      setSelectedIds((current) => (hasLoadedPeriods.current
        ? current.filter((id) => readyIds.includes(id))
        : readyIds));
      hasLoadedPeriods.current = true;
    } catch (error) {
      setPeriods([]);
      setMessage({ open: true, color: 'red', messageInfo: `Erreur de chargement des bases : ${error.message}` });
    } finally {
      if (showLoader) setIsLoading(false);
    }
  }, []);

  useEffect(() => { loadPeriods(); }, [loadPeriods]);

  useEffect(() => {
    setStatusFilter(getStatusFromSearch(location.search));
    if (location.state?.selectReady && handledNavigationKey.current !== location.key) {
      handledNavigationKey.current = location.key;
      setFileFilter('');
      setPeriodFilter('');
      setImporterFilter('');
      setSelectedIds(periods.filter(isReadyPeriod).map((period) => Number(period.part_period_id)));
      setPaginationModel((current) => ({ ...current, page: 0 }));
    }
  }, [location.key, location.search, location.state, periods]);

  const summaryCards = useMemo(() => {
    const syncedCount = periods.filter((period) => isSyncedPeriod(period.is_sync)).length;
    return [
      { label: 'Bases importées', value: periods.length, hint: '', status: 'ALL' },
      { label: 'Synchronisées', value: syncedCount, hint: '', status: 'SYNCED' },
      { label: 'En attente', value: periods.filter(isReadyPeriod).length, hint: 'Imports prêts à synchroniser.', status: 'READY_FOR_SYNC' },
      {
        label: 'Partenaires',
        value: periods.reduce((sum, period) => sum + Number(period.partner_count || 0), 0),
        hint: `${formatCount(periods.reduce((sum, period) => sum + Number(period.marche_count || 0), 0))} marchés consolidés.`,
      },
    ];
  }, [periods]);

  const listedPeriods = periods;
  const periodOptions = useMemo(() => Array.from(new Set(
    listedPeriods.map((period) => readText(period.part_period_label, 'Période sans libellé')),
  )).sort((a, b) => a.localeCompare(b, 'fr')), [listedPeriods]);
  const importerOptions = useMemo(() => {
    const importers = new Map();
    listedPeriods.forEach((period) => {
      importers.set(String(period.user_id), readText(period.imported_by, 'Utilisateur inconnu'));
    });
    return Array.from(importers, ([id, name]) => ({ id, name }))
      .sort((a, b) => a.name.localeCompare(b.name, 'fr'));
  }, [listedPeriods]);
  const visiblePeriods = useMemo(() => listedPeriods.filter((period) => (
    (statusFilter === 'ALL'
      || (statusFilter === 'SYNCED' ? isSyncedPeriod(period.is_sync)
        : statusFilter === 'READY_FOR_SYNC' ? isReadyPeriod(period)
          : !isSyncedPeriod(period.is_sync) && period.import_status === statusFilter))
    && String(period.file_name || '').toLocaleLowerCase('fr').includes(fileFilter.trim().toLocaleLowerCase('fr'))
    && (!periodFilter || readText(period.part_period_label, 'Période sans libellé') === periodFilter)
    && (!importerFilter || String(period.user_id) === importerFilter)
  )), [listedPeriods, statusFilter, fileFilter, periodFilter, importerFilter]);
  const visibleReadyIds = useMemo(() => visiblePeriods.filter(isReadyPeriod)
    .map((period) => Number(period.part_period_id)), [visiblePeriods]);
  const selectedForSync = useMemo(() => selectedIds.filter((id) => visibleReadyIds.includes(id)),
    [selectedIds, visibleReadyIds]);

  useEffect(() => {
    setSelectedIds((current) => {
      const next = current.filter((id) => visibleReadyIds.includes(id));
      return next.length === current.length ? current : next;
    });
  }, [visibleReadyIds]);

  const selectStatus = (status) => {
    navigate(`/partner/database?status=${status}`, { state: null });
    setStatusFilter(status);
    setPaginationModel((current) => ({ ...current, page: 0 }));
  };

  const updateFilter = (setter) => (event) => {
    setter(event.target.value);
    setPaginationModel((current) => ({ ...current, page: 0 }));
  };
  const resetFilters = () => {
    selectStatus('READY_FOR_SYNC');
    setFileFilter('');
    setPeriodFilter('');
    setImporterFilter('');
    setSelectedIds(periods.filter(isReadyPeriod).map((period) => Number(period.part_period_id)));
    setPaginationModel((current) => ({ ...current, page: 0 }));
  };

  const handleSynchronize = useCallback(async (periodIds) => {
    const result = await PartnerService.synchronizePeriods(periodIds);
    const syncedIds = new Set((result?.data?.synced || []).map((period) => Number(period.part_period_id)));
    setSelectedIds((current) => current.filter((id) => !syncedIds.has(id)));
    await loadPeriods({ showLoader: false });
    window.dispatchEvent(new Event('partner:refresh-unsynced-periods'));
    return result;
  }, [loadPeriods]);

  const handleDownload = useCallback(async (period) => {
    try {
      await PartnerService.downloadPeriod(period.part_period_id, readText(period.file_name, 'base-partenaire.xlsx'));
    } catch (error) {
      setMessage({ open: true, color: 'red', messageInfo: 'Impossible de télécharger le fichier de cette base.' });
    }
  }, []);

  const handleRemove = useCallback(async () => {
    if (!periodToRemove) return;
    setRemovingPeriodId(periodToRemove.part_period_id);
    try {
      const result = await PartnerService.removePeriod(periodToRemove.part_period_id, true);
      if (!result?.status) throw new Error(result?.message || 'Retrait impossible');
      setPeriodToRemove(null);
      setMessage({ open: true, color: 'green', messageInfo: result.message || 'Base retirée.' });
      await loadPeriods({ showLoader: false });
      window.dispatchEvent(new Event('partner:refresh-unsynced-periods'));
    } catch (error) {
      setMessage({ open: true, color: 'red', messageInfo: error.message || 'Retrait impossible.' });
    } finally {
      setRemovingPeriodId(null);
    }
  }, [loadPeriods, periodToRemove]);

  const columns = useMemo(() => [
    {
      field: 'select',
      headerName: '',
      width: 58,
      sortable: false,
      filterable: false,
      renderHeader: () => (
        <Checkbox
          size="small"
          inputProps={{ 'aria-label': 'Sélectionner tous les fichiers prêts affichés' }}
          checked={visibleReadyIds.length > 0 && visibleReadyIds.every((id) => selectedIds.includes(id))}
          indeterminate={visibleReadyIds.some((id) => selectedIds.includes(id))
            && !visibleReadyIds.every((id) => selectedIds.includes(id))}
          disabled={visibleReadyIds.length === 0}
          onChange={(event) => setSelectedIds(event.target.checked ? visibleReadyIds : [])}
          sx={{ color: '#fff', '&.Mui-checked, &.MuiCheckbox-indeterminate': { color: '#d5ad3f' } }}
        />
      ),
      renderCell: ({ row }) => (
        <Checkbox
          size="small"
          inputProps={{ 'aria-label': `Sélectionner ${row.file_name || 'la période'}` }}
          checked={selectedIds.includes(Number(row.part_period_id))}
          disabled={!isReadyPeriod(row)}
          onChange={(event) => {
            const id = Number(row.part_period_id);
            setSelectedIds((current) => (event.target.checked
              ? [...current, id]
              : current.filter((selectedId) => selectedId !== id)));
          }}
          onClick={(event) => event.stopPropagation()}
        />
      ),
    },
    {
      field: 'part_period_label',
      headerName: 'Période',
      minWidth: 180,
      flex: 1,
      valueGetter: (value, row) => readText(row?.part_period_label, 'Période sans libellé'),
    },
    {
      field: 'file_name',
      headerName: 'Fichier',
      minWidth: 220,
      flex: 1.3,
      valueGetter: (value, row) => readText(row?.file_name, 'Fichier inconnu'),
    },
    {
      field: 'imported_by',
      headerName: 'Importateur',
      minWidth: 170,
      flex: 1,
      valueGetter: (value, row) => readText(row?.imported_by, 'Utilisateur inconnu'),
    },
    {
      field: 'importer_direction',
      headerName: 'Direction de l’importateur',
      minWidth: 190,
      flex: 1,
      valueGetter: (value, row) => readText(row?.importer_direction),
    },
    {
      field: 'sync_user_name',
      headerName: 'Synchronisé par',
      minWidth: 170,
      flex: 1,
      valueGetter: (value, row) => (
        isSyncedPeriod(row?.is_sync) ? readText(row?.sync_user_name, '-') : '-'
      ),
    },
    {
      field: 'created_at',
      headerName: 'Date import',
      minWidth: 130,
      flex: 0.7,
      valueFormatter: (value) => formatDate(value),
    },
    {
      field: 'import_status',
      headerName: 'Statut',
      minWidth: 175,
      renderCell: ({ row }) => (
        <Chip
          size="small"
          label={isSyncedPeriod(row.is_sync) ? STATUS_LABELS.SYNCED : (STATUS_LABELS[row.import_status] || 'Statut inconnu')}
          sx={isSyncedPeriod(row.is_sync)
            ? { backgroundColor: '#e5f5ef', color: '#047857', fontWeight: 700 }
            : { backgroundColor: '#fff3dc', color: '#9a6200', fontWeight: 700 }}
        />
      ),
    },
    { field: 'marche_count', headerName: 'Marchés', width: 90, align: 'right', headerAlign: 'right', valueFormatter: (value) => formatCount(value) },
    { field: 'partner_count', headerName: 'Partenaires', width: 105, align: 'right', headerAlign: 'right', valueFormatter: (value) => formatCount(value) },
    {
      field: 'actions',
      headerName: 'Actions',
      width: 140,
      sortable: false,
      filterable: false,
      renderCell: ({ row }) => (
        <Box sx={{ display: 'flex', alignItems: 'center', height: '100%' }}>
          <Tooltip title="Télécharger le fichier">
            <span>
              <IconButton size="small" aria-label={`Télécharger ${row.file_name || 'le fichier'}`} disabled={!row.file_name || !row.source_file} onClick={() => handleDownload(row)}>
                <GetApp fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
          {isReadyPeriod(row) && (
            <Tooltip title="Synchroniser">
              <span>
                <IconButton size="small" aria-label={`Synchroniser ${row.file_name || 'la période'}`} disabled={Number(row.is_default) === 1} onClick={() => setSyncIds([Number(row.part_period_id)])}>
                  <Sync fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
          )}
          <Tooltip title="Retirer">
            <span>
              <IconButton size="small" color="error" aria-label={`Retirer ${row.file_name || 'la période'}`} disabled={Number(row.is_default) === 1} onClick={() => setPeriodToRemove(row)}>
                <DeleteOutline fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
        </Box>
      ),
    },
  ], [handleDownload, selectedIds, visibleReadyIds]);

  return (
    <PartnerMainContainer>
      <Message
        open={message.open}
        color={message.color}
        messageInfo={message.messageInfo}
        handleClose={() => setMessage((current) => ({ ...current, open: false }))}
      />

      <div className={classes.headerRow}>
        <TitlePartner title="Bases importées" sub={isLoading ? '' : `(${formatCount(visiblePeriods.length)} base(s))`} />
      </div>

      <div className={classes.summaryGrid}>
        {summaryCards.map((card) => (
          <Box
            key={card.label}
            component={card.status ? 'button' : 'div'}
            type={card.status ? 'button' : undefined}
            onClick={card.status ? () => selectStatus(card.status) : undefined}
            className={`${classes.summaryCard} ${card.status ? classes.summaryButton : ''} ${card.status === statusFilter ? classes.summaryActive : ''}`}
          >
            <div className={classes.summaryLabel}>{card.label}</div>
            <div className={classes.summaryValue}>{formatCount(card.value)}</div>
            <div className={classes.summaryHint}>{card.hint}</div>
          </Box>
        ))}
      </div>

      <Box sx={{ border: `1px solid ${partnerTableColors.border}`, borderRadius: '8px', p: 2, mb: 2, backgroundColor: '#fff' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2, flexWrap: 'wrap', mb: 2 }}>
          <Typography sx={{ color: partnerTableColors.dark, fontSize: 12, fontWeight: 800, letterSpacing: 1 }}>FILTRES</Typography>
          <Button variant="outlined" onClick={resetFilters} sx={partnerFilterButtonSx}>Réinitialiser</Button>
        </Box>
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, minmax(0, 1fr))', lg: 'repeat(4, minmax(0, 1fr))' }, gap: 1.5 }}>
          <TextField select label="Statut" size="small" value={statusFilter} onChange={(event) => selectStatus(event.target.value)} sx={partnerFilterFieldSx}>
            {Object.entries(STATUS_LABELS).map(([value, label]) => <MenuItem key={value} value={value}>{label}</MenuItem>)}
          </TextField>
          <TextField label="Nom du fichier" size="small" value={fileFilter} onChange={updateFilter(setFileFilter)} sx={partnerFilterFieldSx} />
          <TextField select label="Période" size="small" value={periodFilter} onChange={updateFilter(setPeriodFilter)} sx={partnerFilterFieldSx}>
            <MenuItem value="">Toutes les périodes</MenuItem>
            {periodOptions.map((period) => <MenuItem key={period} value={period}>{period}</MenuItem>)}
          </TextField>
          <TextField select label="Importateur" size="small" value={importerFilter} onChange={updateFilter(setImporterFilter)} sx={partnerFilterFieldSx}>
            <MenuItem value="">Tous les importateurs</MenuItem>
            {importerOptions.map((importer) => <MenuItem key={importer.id} value={importer.id}>{importer.name}</MenuItem>)}
          </TextField>
        </Box>
      </Box>

      {isLoading ? (
        <div className={classes.stateCard}>
          <div className={classes.loadingRow}><CircularProgress size={20} /><span>Chargement des bases importées...</span></div>
        </div>
      ) : (
        <Box sx={{ border: `1px solid ${partnerTableColors.border}`, borderRadius: '8px', backgroundColor: '#fff', overflow: 'hidden' }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 1, flexWrap: 'wrap', p: 1.5 }}>
            <Typography sx={{ color: partnerTableColors.dark, fontSize: 14, fontWeight: 700 }}>Bases synchronisées et prêtes à synchroniser</Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
              <Typography sx={{ color: partnerTableColors.text, fontSize: 12 }}>
                {formatCount(visiblePeriods.length)} fichier(s) · {formatCount(selectedForSync.length)} sélectionné(s)
              </Typography>
              <Button
                variant="contained"
                startIcon={<Sync fontSize="small" />}
                disabled={selectedForSync.length === 0}
                onClick={() => setSyncIds([...selectedForSync])}
                sx={{ backgroundColor: partnerTableColors.dark, textTransform: 'none', '&:hover': { backgroundColor: '#102d49' } }}
              >
                Synchroniser
              </Button>
            </Box>
          </Box>
          <Box sx={{ height: 560, width: '100%' }}>
            <DataGrid
              rows={visiblePeriods}
              columns={columns}
              getRowId={(row) => row.part_period_id}
              rowHeight={48}
              paginationModel={paginationModel}
              onPaginationModelChange={setPaginationModel}
              pageSizeOptions={[10, 15, 30, 50]}
              disableRowSelectionOnClick
              localeText={frFR.components.MuiDataGrid.defaultProps.localeText}
              sx={partnerDataGridSx}
            />
          </Box>
        </Box>
      )}

      <SynchronizeDialog
        open={syncIds.length > 0}
        onClose={() => setSyncIds([])}
        periodIds={syncIds}
        onSynchronize={handleSynchronize}
      />

      <Dialog open={Boolean(periodToRemove)} onClose={() => removingPeriodId === null && setPeriodToRemove(null)} fullWidth maxWidth="sm">
        <DialogTitle>Retirer cette base ?</DialogTitle>
        <DialogContent dividers>
          <Typography gutterBottom><strong>{readText(periodToRemove?.part_period_label, 'Base sélectionnée')}</strong> sera retirée.</Typography>
          <Typography color="textSecondary">
            {periodToRemove && isSyncedPeriod(periodToRemove.is_sync)
              ? 'Les données synchronisées rattachées à cette période seront aussi supprimées.'
              : 'Seuls l’import non synchronisé, son staging et son fichier seront supprimés.'}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPeriodToRemove(null)} disabled={removingPeriodId !== null}>Annuler</Button>
          <Button color="secondary" variant="contained" onClick={handleRemove} disabled={removingPeriodId !== null}>
            {removingPeriodId !== null ? <CircularProgress size={16} color="inherit" /> : 'Retirer'}
          </Button>
        </DialogActions>
      </Dialog>
    </PartnerMainContainer>
  );
};

export default DatabaseView;
